/**
 * Souqli smart listing agent.
 *
 * A single, deterministic classifier that reads a seller's plain-language
 * product summary and decides three things at once:
 *
 *   1. category   - which marketplace category the item belongs in
 *   2. condition  - the 5-point grade (NEW_TAGS -> FAIR) stored on the listing
 *   3. family     - the product family, which decides the *vocabulary* used to
 *                   describe condition. A phone is never "worn" - it is
 *                   "sealed", "open box" or "used". A fragrance is graded by how
 *                   full the bottle is. A watch or bag is "pre-owned".
 *
 * It also surfaces signals a description writer cares about: whether the item
 * is factory-sealed / deadstock, and (for fragrance) the fill percentage.
 *
 * No external API - pure heuristics, so it runs instantly, offline, and free,
 * and is shared by both the AI listing tool and the Sell page.
 */
import { garmentType, type Garment } from '@/lib/catalog';

export type Condition = 'NEW_TAGS' | 'LIKE_NEW' | 'EXCELLENT' | 'GOOD' | 'FAIR';

/**
 * Product families - each carries its own condition vocabulary. Kept fine
 * grained so the options a seller sees actually fit the item: a phone grades by
 * screen/scratches, trainers by box/creasing (deadstock -> beaters), a scent by
 * how full the bottle is. `tech` is the catch-all for other gadgets.
 */
export type Family =
  | 'apparel'
  | 'footwear'
  | 'phone'
  | 'laptop'
  | 'tech'
  | 'watch'
  | 'bag'
  | 'fragrance';

export interface ConditionOption {
  v: Condition;
  label: string;
  body: string;
  dots: number;
}

export interface Classification {
  category: string;
  garment: Garment;
  family: Family;
  condition: Condition;
  /** True when the item is factory-sealed / new-in-box / deadstock. */
  sealed: boolean;
  /** Fragrance only: approximate bottle fill, 0-100, when stated or inferred. */
  fillPercent?: number;
  /** Human-readable reasons behind the decision (for tooltips / debugging). */
  signals: string[];
}

/* ─────────────────────────── Family mapping ─────────────────────────── */

const CATEGORY_FAMILY: Record<string, Family> = {
  electronics: 'tech',
  watches: 'watch',
  bags: 'bag',
  accessories: 'bag',
  fragrance: 'fragrance',
  womenswear: 'apparel',
  menswear: 'apparel',
  shoes: 'footwear',
  kids: 'apparel',
  traditional: 'apparel',
};

/** Families that grade like a gadget (sealed / used, not worn). */
const TECH_FAMILIES = new Set<Family>(['phone', 'laptop', 'tech']);

/**
 * Which condition vocabulary an item should use. The specific garment wins over
 * the broad category, so a phone in "electronics" gets phone options rather than
 * generic tech ones, and trainers in "shoes" get footwear options.
 */
export function productFamily(category?: string, garment?: Garment): Family {
  if (garment === 'phone') return 'phone';
  if (garment === 'laptop') return 'laptop';
  if (garment === 'watch') return 'watch';
  if (garment === 'bag') return 'bag';
  if (garment === 'shoes') return 'footwear';
  if (category && CATEGORY_FAMILY[category]) return CATEGORY_FAMILY[category];
  return 'apparel';
}

/** Kept for backwards compatibility - true for any gadget-graded family. */
export function isGadget(category?: string, garment?: Garment): boolean {
  return TECH_FAMILIES.has(productFamily(category, garment));
}

/* ─────────────────────── Category detection ─────────────────────── */

const GARMENT_CATEGORY: Record<Garment, string> = {
  phone: 'electronics', laptop: 'electronics', watch: 'watches', bag: 'bags', shoes: 'shoes',
  traditional: 'womenswear', dress: 'womenswear', skirt: 'womenswear', top: 'womenswear',
  shirt: 'womenswear', tshirt: 'womenswear', knitwear: 'womenswear', blazer: 'menswear',
  suit: 'menswear', jacket: 'womenswear', coat: 'womenswear', trousers: 'womenswear',
  jeans: 'womenswear', shorts: 'womenswear', accessory: 'accessories', other: 'womenswear',
};

// Fragrance is often named only by brand + line + bottle size (no word
// "perfume"), so a millilitre/ounce volume or a well-known scent line counts.
const FRAGRANCE_RE = /\b(perfume|cologne|fragrance|eau de|parfum|\bedt\b|\bedp\b|aftershave|\d{2,4}\s?ml|\d(?:\.\d)?\s?oz|oud|aventus|sauvage|bleu de chanel|eros|acqua di gio)\b/;

export function detectCategory(t: string, garment?: Garment): string {
  const lower = t.toLowerCase();
  if (FRAGRANCE_RE.test(lower)) return 'fragrance';
  if (/\b(kids?|child(ren)?|toddler|baby|infant|boys?|girls?)\b/.test(lower)) return 'kids';
  const g = garment ?? garmentType({ title: t });
  let cat = GARMENT_CATEGORY[g] ?? 'womenswear';
  const apparel = !['electronics', 'watches', 'bags', 'shoes', 'accessories'].includes(cat);
  if (apparel) {
    if (/\b(men'?s?|mens|male|him|gent'?s?)\b/.test(lower)) cat = 'menswear';
    else if (/\b(women'?s?|womens|ladies|female|her)\b/.test(lower)) cat = 'womenswear';
  }
  return cat;
}

/* ─────────────────────── Signal detection ─────────────────────── */

// Factory-sealed / never-opened / deadstock.
const SEALED_RE = /\b(sealed|factory[- ]?sealed|shrink[- ]?wrap(?:ped)?|cellophane|unopened|bnib|brand[- ]?new in box|dead[- ]?stock|\bnib\b|still (?:in|with) (?:the )?(?:box|wrapper|packaging|cellophane))\b/i;
// Opened but effectively unused.
const OPEN_BOX_RE = /\b(open[- ]?box|opened (?:but )?(?:unused|barely used)|display (?:model|unit)|ex[- ]?display)\b/i;
// Pre-owned / used language.
const PREOWNED_RE = /\b(pre[- ]?(?:owned|loved)|preloved|second[- ]?hand|used|worn|carried)\b/i;

/** Parse a stated fill level for fragrance, e.g. "80% full", "90% remaining". */
function detectFill(t: string): number | undefined {
  const m = t.match(/(\d{1,3})\s?%\s*(?:full|remaining|left|used)?/i);
  if (!m) return undefined;
  let pct = Number(m[1]);
  if (!Number.isFinite(pct)) return undefined;
  pct = Math.max(0, Math.min(100, pct));
  // "20% used" means 80% remaining.
  if (/%\s*used/i.test(m[0])) pct = 100 - pct;
  return pct;
}

/* ─────────────────────── Condition detection ─────────────────────── */

/**
 * Grade the 5-point condition from free text. Order matters: the strongest
 * signal wins. Sealed / deadstock always reads as brand new.
 */
export function detectCondition(t: string): Condition {
  const lower = t.toLowerCase();
  if (SEALED_RE.test(lower) || /\b(brand[- ]?new|new with tags?|bnwt|unworn|unused|never (?:worn|used))\b/.test(lower)) return 'NEW_TAGS';
  if (OPEN_BOX_RE.test(lower) || /\b(like[- ]?new|worn once|used once|barely (?:worn|used)|mint condition|as new)\b/.test(lower)) return 'LIKE_NEW';
  if (/\b(excellent|worn twice|used twice|hardly (?:worn|used)|great condition|very good)\b/.test(lower)) return 'EXCELLENT';
  // Heavy / structural damage grades down to FAIR. A single light cosmetic mark
  // (one scratch, scuff, small stain, slight fade) is not FAIR - it falls
  // through to GOOD below.
  if (/\b(well[- ]?(?:loved|used)|heavily used|lots of wear|worn out|badly|multiple (?:scratches|scuffs|marks|chips)|deep (?:scratch|scuffs?|gouge)|crack(?:ed)?|chipped|torn|\brip(?:ped)?\b)\b/.test(lower)) return 'FAIR';
  if (/\b(good condition|light wear|used|pre[- ]?(?:owned|loved)|second[- ]?hand|some wear|scratch|scuff|stain|mark|fade|faded|tear)\b/.test(lower)) return 'GOOD';
  return 'LIKE_NEW';
}

/** Map a fragrance fill percentage to the 5-point grade (labels: 90%+, ~75%, ~50%, under half). */
function conditionFromFill(pct: number, sealed: boolean): Condition {
  if (pct >= 95 && sealed) return 'NEW_TAGS';
  if (pct >= 90) return 'LIKE_NEW';
  if (pct >= 65) return 'EXCELLENT';
  if (pct >= 40) return 'GOOD';
  return 'FAIR';
}

/* ─────────────────────── The agent ─────────────────────── */

/**
 * The smart agent. Reads a plain-language summary and returns the category,
 * condition, family and the signals a description writer needs.
 */
export function classifyListing(text: string): Classification {
  const raw = text ?? '';
  const garment = garmentType({ title: raw });
  const category = detectCategory(raw, garment);
  const family = productFamily(category, garment);
  const signals: string[] = [];

  const sealed = SEALED_RE.test(raw);
  if (sealed) signals.push('Detected factory-sealed / deadstock wording');

  let fillPercent: number | undefined;
  let condition: Condition;

  if (family === 'fragrance') {
    fillPercent = detectFill(raw);
    if (fillPercent !== undefined) {
      condition = conditionFromFill(fillPercent, sealed);
      signals.push(`Bottle fill read as ${fillPercent}%`);
    } else if (sealed) {
      condition = 'NEW_TAGS';
    } else {
      condition = detectCondition(raw);
    }
  } else {
    condition = detectCondition(raw);
  }

  if (!sealed && PREOWNED_RE.test(raw) && condition === 'NEW_TAGS') {
    // "used" beats an accidental new-match when nothing says sealed.
    condition = 'GOOD';
    signals.push('Pre-owned wording downgraded an over-optimistic grade');
  }

  signals.push(`Category: ${category}`, `Family: ${family}`, `Condition: ${condition}`);
  return { category, garment, family, condition, sealed, fillPercent, signals };
}

/* ─────────────────────── Condition vocabulary ─────────────────────── */

const VOCAB: Record<Family, Record<Condition, { label: string; body: string; dots: number }>> = {
  apparel: {
    NEW_TAGS:  { label: 'Never worn',   body: 'Tags still attached',        dots: 5 },
    LIKE_NEW:  { label: 'Worn once',    body: 'Tried on or worn once',      dots: 5 },
    EXCELLENT: { label: 'Worn twice',   body: 'Minimal signs of wear',      dots: 4 },
    GOOD:      { label: 'Good',         body: 'Light wear, looks great',    dots: 3 },
    FAIR:      { label: 'Well loved',   body: 'Clear signs of wear',        dots: 2 },
  },
  footwear: {
    NEW_TAGS:  { label: 'Brand new in box', body: 'Deadstock, never worn',        dots: 5 },
    LIKE_NEW:  { label: 'Worn once',        body: 'Tried on or worn once, like new', dots: 5 },
    EXCELLENT: { label: 'Worn a few times', body: 'Light creasing, well kept',    dots: 4 },
    GOOD:      { label: 'Used - good',      body: 'Visible wear, plenty of life',  dots: 3 },
    FAIR:      { label: 'Well worn',        body: 'Heavy wear, priced to suit',   dots: 2 },
  },
  phone: {
    NEW_TAGS:  { label: 'Sealed',          body: 'Brand new, factory sealed',    dots: 5 },
    LIKE_NEW:  { label: 'Like new',        body: 'Opened, flawless, no marks',   dots: 5 },
    EXCELLENT: { label: 'Light scratches', body: 'Minor marks, screen clean',    dots: 4 },
    GOOD:      { label: 'Used - good',     body: 'Visible wear, fully working',  dots: 3 },
    FAIR:      { label: 'Heavy wear',      body: 'Cracks or dents, still working', dots: 2 },
  },
  laptop: {
    NEW_TAGS:  { label: 'Sealed',       body: 'Brand new, factory sealed',      dots: 5 },
    LIKE_NEW:  { label: 'Like new',     body: 'Opened, barely used',            dots: 5 },
    EXCELLENT: { label: 'Light wear',   body: 'Minor marks on lid or keys',     dots: 4 },
    GOOD:      { label: 'Used - good',  body: 'Visible wear, works perfectly',  dots: 3 },
    FAIR:      { label: 'Heavy wear',   body: 'Dents or scratches, fully working', dots: 2 },
  },
  tech: {
    NEW_TAGS:  { label: 'Sealed',       body: 'Brand new, factory sealed',  dots: 5 },
    LIKE_NEW:  { label: 'Open box',     body: 'Opened, barely used',        dots: 5 },
    EXCELLENT: { label: 'Lightly used', body: 'Minimal signs of use',       dots: 4 },
    GOOD:      { label: 'Used',         body: 'Works perfectly, light wear', dots: 3 },
    FAIR:      { label: 'Well used',    body: 'Clear signs of use, working', dots: 2 },
  },
  watch: {
    NEW_TAGS:  { label: 'New, unworn',  body: 'Unworn, full set',           dots: 5 },
    LIKE_NEW:  { label: 'Like new',     body: 'Barely worn, no marks',      dots: 5 },
    EXCELLENT: { label: 'Excellent',    body: 'Serviced, minimal marks',    dots: 4 },
    GOOD:      { label: 'Pre-owned',    body: 'Honest wear, keeps time',    dots: 3 },
    FAIR:      { label: 'Fair',         body: 'Visible wear, priced to suit', dots: 2 },
  },
  bag: {
    NEW_TAGS:  { label: 'New with tags', body: 'Unused, tags attached',     dots: 5 },
    LIKE_NEW:  { label: 'Like new',      body: 'Carried once or twice',     dots: 5 },
    EXCELLENT: { label: 'Excellent',     body: 'Barely carried',            dots: 4 },
    GOOD:      { label: 'Good',          body: 'Light wear on corners',     dots: 3 },
    FAIR:      { label: 'Well loved',    body: 'Visible wear, priced to suit', dots: 2 },
  },
  fragrance: {
    NEW_TAGS:  { label: 'Sealed',        body: 'Cellophane sealed, full',   dots: 5 },
    LIKE_NEW:  { label: '90%+ full',     body: 'Opened, almost full',       dots: 5 },
    EXCELLENT: { label: '~75% full',     body: 'A few sprays used',         dots: 4 },
    GOOD:      { label: '~50% full',     body: 'About half remaining',      dots: 3 },
    FAIR:      { label: 'Under half',    body: 'Well used, priced low',     dots: 2 },
  },
};

/** Condition radio/select options with labels appropriate to the item type. */
export function conditionOptions(category?: string, garment?: Garment): ConditionOption[] {
  const fam = productFamily(category, garment);
  const table = VOCAB[fam];
  return (['NEW_TAGS', 'LIKE_NEW', 'EXCELLENT', 'GOOD', 'FAIR'] as Condition[])
    .map((v) => ({ v, ...table[v] }));
}

/** One-line human label describing the family's condition axis. */
export function conditionAxisLabel(category?: string, garment?: Garment): string {
  switch (productFamily(category, garment)) {
    case 'phone':     return 'sealed / scratches for phones';
    case 'laptop':    return 'sealed / wear for laptops';
    case 'tech':      return 'sealed / used for tech';
    case 'footwear':  return 'box / creasing for trainers';
    case 'watch':     return 'pre-owned for watches';
    case 'bag':       return 'pre-owned for bags';
    case 'fragrance': return 'by how full the bottle is';
    default:          return 'wear-based for clothing';
  }
}

/**
 * Condition phrasing for descriptions - family-aware, and refined by the
 * sealed flag and fragrance fill level so copy reads naturally: "sealed and
 * unopened" for tech, "pre-owned" for a watch, "roughly 80% full" for a scent,
 * "worn once" for a coat.
 */
export function conditionPhrase(
  condition: Condition,
  category?: string,
  garment?: Garment,
  opts: { sealed?: boolean; fillPercent?: number } = {},
): { label: string; body: string } {
  const fam = productFamily(category, garment);

  if (fam === 'fragrance') {
    if (condition === 'NEW_TAGS' || opts.sealed) {
      return { label: 'brand new and sealed', body: 'cellophane still on, full bottle' };
    }
    if (opts.fillPercent !== undefined) {
      return { label: `around ${opts.fillPercent}% full`, body: 'opened and tested, priced to match' };
    }
    const frag: Record<Condition, { label: string; body: string }> = {
      NEW_TAGS:  { label: 'brand new and sealed', body: 'cellophane still on, full bottle' },
      LIKE_NEW:  { label: 'over 90% full',        body: 'opened but barely used' },
      EXCELLENT: { label: 'around 75% full',      body: 'only a few sprays used' },
      GOOD:      { label: 'around half full',     body: 'plenty of wears left' },
      FAIR:      { label: 'under half full',      body: 'well used, priced low' },
    };
    return frag[condition];
  }

  if (fam === 'phone') {
    if (condition === 'NEW_TAGS' || opts.sealed) {
      return { label: 'brand new and sealed', body: 'unopened, in original packaging' };
    }
    const phone: Record<Condition, { label: string; body: string }> = {
      NEW_TAGS:  { label: 'brand new and sealed', body: 'unopened, in original packaging' },
      LIKE_NEW:  { label: 'like new',             body: 'opened but flawless, no scratches or marks' },
      EXCELLENT: { label: 'excellent condition',  body: 'only light scratches, screen and body clean' },
      GOOD:      { label: 'good condition',       body: 'visible wear, fully working with no faults' },
      FAIR:      { label: 'heavy wear',           body: 'cracks or dents but still fully working, priced to reflect it' },
    };
    return phone[condition];
  }

  if (fam === 'laptop') {
    if (condition === 'NEW_TAGS' || opts.sealed) {
      return { label: 'brand new and sealed', body: 'unopened, in original packaging' };
    }
    const laptop: Record<Condition, { label: string; body: string }> = {
      NEW_TAGS:  { label: 'brand new and sealed', body: 'unopened, in original packaging' },
      LIKE_NEW:  { label: 'like new',             body: 'opened and barely used, no marks' },
      EXCELLENT: { label: 'excellent condition',  body: 'only minor marks on the lid or keys' },
      GOOD:      { label: 'good condition',       body: 'visible wear, works perfectly' },
      FAIR:      { label: 'heavy wear',           body: 'dents or scratches but fully working, priced to reflect it' },
    };
    return laptop[condition];
  }

  if (fam === 'tech') {
    if (condition === 'NEW_TAGS' || opts.sealed) {
      return { label: 'brand new and sealed', body: 'unopened, in original packaging' };
    }
    const tech: Record<Condition, { label: string; body: string }> = {
      NEW_TAGS:  { label: 'brand new and sealed', body: 'unopened, in original packaging' },
      LIKE_NEW:  { label: 'open box',             body: 'opened and barely used, no marks' },
      EXCELLENT: { label: 'excellent condition',  body: 'lightly used, only minimal signs of use' },
      GOOD:      { label: 'good condition',       body: 'used and fully working' },
      FAIR:      { label: 'well used',            body: 'clear signs of use, fully working, priced to reflect it' },
    };
    return tech[condition];
  }

  if (fam === 'footwear') {
    if (condition === 'NEW_TAGS' || opts.sealed) {
      return { label: 'brand new in box', body: 'deadstock, never worn, with the original box' };
    }
    const footwear: Record<Condition, { label: string; body: string }> = {
      NEW_TAGS:  { label: 'brand new in box',   body: 'deadstock, never worn, with the original box' },
      LIKE_NEW:  { label: 'worn once',          body: 'tried on or worn once, no visible wear' },
      EXCELLENT: { label: 'worn a few times',   body: 'light creasing, very well kept' },
      GOOD:      { label: 'used, good condition', body: 'visible wear with plenty of life left' },
      FAIR:      { label: 'well worn',          body: 'heavy wear, priced to reflect it' },
    };
    return footwear[condition];
  }

  if (fam === 'watch' || fam === 'bag') {
    const noun = fam === 'watch' ? 'watch' : 'piece';
    const carried = fam === 'watch' ? 'worn' : 'carried';
    const preowned: Record<Condition, { label: string; body: string }> = {
      NEW_TAGS:  { label: fam === 'bag' ? 'brand new with tags' : 'brand new, unworn', body: `unused ${noun}, full set` },
      LIKE_NEW:  { label: 'like new',            body: `${carried} once or twice, no marks` },
      EXCELLENT: { label: 'excellent condition', body: `barely ${carried}, only minimal marks` },
      GOOD:      { label: 'pre-owned, good condition', body: 'honest, gentle wear' },
      FAIR:      { label: 'pre-owned',           body: 'visible wear, priced to reflect it' },
    };
    return preowned[condition];
  }

  // apparel
  const apparel: Record<Condition, { label: string; body: string }> = {
    NEW_TAGS:  { label: 'brand new with tags', body: 'never worn, tags still attached' },
    LIKE_NEW:  { label: 'like new',            body: 'worn once with no visible wear' },
    EXCELLENT: { label: 'excellent condition', body: 'worn twice, only minimal signs of wear' },
    GOOD:      { label: 'good condition',      body: 'light wear consistent with gentle use' },
    FAIR:      { label: 'well loved',          body: 'clear signs of wear, priced to reflect it' },
  };
  return apparel[condition];
}
